import React, { useState } from 'react';
import styled from 'styled-components';
import CalendarWidget from './CalendarWidget';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import { CrossIcon } from '../Icons';
import { useTranslation } from 'react-i18next';
import { startOfDay, endOfDay, getFreeSlots } from '../../../calendarUtils';
import dateFormat from 'dateformat';
import { environment } from '../../../utils';

const { REACT_APP_START_DATE } = environment;

const PropositionContainer = styled.div`
  padding: 0.5rem 0 0.5rem 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid ${(p) => p.theme.color.boldText};
`;

const PropositionRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 1rem;

  @media screen and (min-width: 700px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Column = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-start;
`;

const Label = styled.label`
  font-size: 0.9rem;
`;

const RemoveButton = styled.span`
  display: flex;
  max-width: 30px;
  margin: 0.5rem;
  align-self: flex-end;
  cursor: pointer;

  &:hover svg path {
    fill: ${(p) => p.theme.color.boldText};
  }

  svg {
    width: 25px;
    height: 25px;

    path {
      fill: ${(p) => p.theme.color.boldText};
    }
  }
`;

const FreeSlotsContainer = styled.div`
  margin-bottom: 1rem;
`;

const FreeSlotList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const FreeSlotButton = styled.button`
  background-color: ${(p) => p.theme.color.white10};
  border: 1px solid ${(p) => p.theme.color.boldText};
  color: ${(p) => p.theme.color.boldText};
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-family: monospace;

  &:hover {
    background-color: ${(p) => p.theme.color.boldText};
    color: white;
  }
`;

const parseDateStr = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split('.').map(Number);
  // Note: month is 0-indexed in JS Date
  return new Date(year, month - 1, day);
};

const Proposition = ({
  onUpdateValue,
  startTime,
  endTime,
  date,
  onDelete,
  isPostSignup,
  programmes,
}) => {
  const [showCalendar, setShowCalendar] = useState(true);
  const { t } = useTranslation();

  const onDatePickerClick = () => {
    setShowCalendar(!showCalendar);
  };

  const onDateSelect = (dateDay) => {
    const year = new Date(REACT_APP_START_DATE).getFullYear();
    const date = `${dateDay}.04.${year}`;
    setShowCalendar(false);

    onUpdateValue({ date, startTime, endTime });
  };

  const onStartTimeChange = (event) => {
    const startTime = event.target.value;
    onUpdateValue({ date, startTime, endTime });
  };

  const onEndTimeChange = (event) => {
    const endTime = event.target.value;
    onUpdateValue({ date, startTime, endTime });
  };

  const remove = () => {
    onDelete();
  };

  const getDayFreeSlots = (dateStr) => {
    if (!isPostSignup || !programmes || !dateStr) return null;

    const parsedDate = parseDateStr(dateStr);
    if (!parsedDate) return null;

    const today = startOfDay(parsedDate);
    const radioStart = new Date(REACT_APP_START_DATE);

    const isFirstDay = today === startOfDay(radioStart);
    const dayStart = isFirstDay
      ? startOfDay(today) + 12 * 60 * 60 * 1000 // skip first 12 hours of first day
      : startOfDay(today);
    const dayEnd = endOfDay(today);

    const previousDay = startOfDay(today - 86400000);
    const previousDayPrograms = programmes[previousDay] || [];
    const currentDayPrograms = programmes[today] || [];

    return getFreeSlots(
      currentDayPrograms,
      dayStart,
      dayEnd,
      previousDayPrograms
    );
  };

  const freeSlots = getDayFreeSlots(date);
  const noFreeSlots =
    isPostSignup && date && freeSlots && freeSlots.length === 0;

  const renderFreeSlots = () => {
    if (!isPostSignup || !programmes || !date || !freeSlots) return null;

    if (freeSlots.length === 0) {
      return (
        <FreeSlotsContainer>
          <Label>{t('signup.propositions.noFreeSlots')}</Label>
        </FreeSlotsContainer>
      );
    }

    return (
      <FreeSlotsContainer>
        <Label>{t('signup.propositions.availableSlots')}</Label>
        <FreeSlotList>
          {freeSlots.map((slot, index) => {
            const s = dateFormat(slot.start, 'HH:00');
            const e = dateFormat(slot.end, 'HH:00');
            return (
              <FreeSlotButton
                key={index}
                type="button"
                onClick={() =>
                  onUpdateValue({ date, startTime: s, endTime: e })
                }
              >
                {s} - {e}
              </FreeSlotButton>
            );
          })}
        </FreeSlotList>
      </FreeSlotsContainer>
    );
  };

  return (
    <>
      <PropositionContainer>
        <Row>
          <DatePicker
            onClick={onDatePickerClick}
            date={date}
            show={showCalendar}
          />
          <RemoveButton onClick={remove}>
            <CrossIcon />
          </RemoveButton>
        </Row>
        {renderFreeSlots()}
        <PropositionRow>
          <CalendarWidget
            onDateSelect={onDateSelect}
            show={showCalendar}
            isPostSignup={isPostSignup}
            programmes={programmes}
          />
          {!noFreeSlots && (
            <Row>
              <Column>
                <Label>{t('signup.propositions.startTime')}</Label>
                <TimePicker onChange={onStartTimeChange} time={startTime} />
              </Column>
              <Column>
                <Label>{t('signup.propositions.endTime')}</Label>
                <TimePicker onChange={onEndTimeChange} time={endTime} />
              </Column>
            </Row>
          )}
        </PropositionRow>
      </PropositionContainer>
    </>
  );
};

export default Proposition;
